import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import { CheckmarkIcon, XIcon } from '../../common'
import { PaymentMethod, RecordStatus, RecordSubstatus } from '../../../models'
import { currencyFormat } from '../../../helpers/currencyCalculation'

import { RecordInput, RecordInputMap, RecordEvent } from './reconciliation'
import InvoiceReconciliationFilters from './filters'

export interface ReconciliationTableProps {
    records: RecordInputMap
    paymentMethods: PaymentMethod[],
    recordStatuses: RecordStatus[],
    /** called when a record input field is edited */
    onRecordUpdated: (event: RecordEvent) => void
    /** whether extraction/drafts exist (enable dropdown) */
    extractionAvailable: boolean
}

export const ReconciliationTable = (props: ReconciliationTableProps) => {
    const [bulkSelectValue, setBulkSelectValue] = useState<string>('')
    const sortRecords = (a: RecordInput, b: RecordInput) => {
        const agencyA = a.record.agencyName
        const agencyB = b.record.agencyName

        const lastNameA = a.record.guestLastName
        const lastNameB = b.record.guestLastName

        return agencyA < agencyB ? -1 :
            agencyA > agencyB ? 1 :
                lastNameA < lastNameB ? -1 :
                    lastNameA > lastNameB ? 1 :
                        0
    }
    const unreconciledRecords = Object.values(props.records).filter((r) => !r.reconciled)
    unreconciledRecords.sort(sortRecords)

    const reconciledRecords = Object.values(props.records).filter((r) => r.reconciled)
    reconciledRecords.sort(sortRecords)

    const selectedRecords = Object.values(props.records).filter((r) => r.selected)
    const editedRecords = Object.values(props.records).filter((r) => Object.keys(r.edited).length > 0)

    const {
        checkInFrom,
        checkInTo,
        yearError,
        isYearInvalid,
        setCheckInFrom,
        setCheckInTo,
        handleYearChange,
        isYearInRange
    } = InvoiceReconciliationFilters();

    const getPaymentMethodName = (paymentMethodId: number) => {
        if (paymentMethodId == null) return 'N/A'
        const method = props.paymentMethods.find((m) => m.id === paymentMethodId)
        return method?.name ?? 'N/A'
    }

    const getStatusName = (statusCode: number ) => {
        const status = props.recordStatuses.find((s) => s.code === Number(statusCode))
        return status?.name ?? ''
    }

    const wasExtracted = (recordInput: RecordInput, field: string) =>
        Array.isArray(recordInput.extracted_data) && recordInput.extracted_data.includes(field)

    const renderOriginalText = (value: React.ReactNode) => (
        <div className="small text-info mt-1 fw-semibold text-center">{value}</div>
    )

    const getConfidenceClass = (confidence: number | undefined): string => {
        if (typeof confidence !== 'number') return ''
        if (confidence >= 80) return 'confidence-high'
        if (confidence >= 60) return 'confidence-medium'
        return 'confidence-low'
    }

    return (
        <div className="reconciliation-table-container">
            <div className="row table-metrics mt-2 align-items-center">
                <div className='col-5 col-md-7 col-lg-8 col-xl-9'>
                    <div className="edited-records">
                        <span>Records modified: <span className="value">{editedRecords.length}</span></span>
                    </div>
                    <div className="selected-records">
                        <span>Records selected: <span className="value">{selectedRecords.length}</span></span>
                    </div>
                </div>
                <div className="col-7 col-md-5 col-lg-4 col-xl-3">
                    <div className="unreconciled-records d-flex justify-content-between">
                        <span className="button-label">Unreconciled: <span className="value">{unreconciledRecords.length}</span></span>
                        <button
                            className='btn btn-outline-success'
                            onClick={(e) => props.onRecordUpdated({ type: 'reconcile', reconciled: true })}
                        >
                            Reconcile All
                        </button>
                    </div>
                    <div className="reconciledRecords d-flex justify-content-between">
                        <span className="button-label">Reconciled: <span className="value">{reconciledRecords.length}</span></span>
                        <button
                            className='btn btn-outline-danger'
                            onClick={(e) => props.onRecordUpdated({ type: 'reconcile', reconciled: false })}
                        >
                            Cancel All
                        </button>
                    </div>
                </div>
            </div>
            <div className="col-12 col-sm-4 pe-sm-2 mb-3">
                <div className="row g-3">
                    <div className="col-4">
                        <label htmlFor="checkInYearFrom">Check-in Year From</label>
                        <input
                            className="form-control"
                            type="text"
                            id="checkInYearFrom"
                            placeholder="YYYY"
                            inputMode="numeric"
                            maxLength={4}
                            value={checkInFrom}
                            onChange={(e) =>
                                handleYearChange(
                                    e.target.value,
                                    setCheckInFrom,
                                )
                            }
                        />
                    </div>
                    <div className="col-4">
                        <label htmlFor="checkInYearTo">Check-in Year To</label>
                        <input
                            className="form-control"
                            type="text"
                            id="checkInYearTo"
                            placeholder="YYYY"
                            inputMode="numeric"
                            maxLength={4}
                            value={checkInTo}
                            onChange={(e) =>
                                handleYearChange(
                                    e.target.value,
                                    setCheckInTo,
                                )
                            }
                        />
                    </div>
                    <div className="col-4 d-flex align-items-end">
                        <button
                            className="btn btn-outline-primary w-100"
                            disabled={isYearInvalid || checkInFrom.length !== 4 || checkInTo.length !== 4}
                            onClick={() => {
                                Object.values(props.records).forEach((recordInput) => {
                                const recordYear = recordInput.record.startDate.year()
                                    const isMatch = isYearInRange(
                                        recordYear,
                                        checkInFrom,
                                        checkInTo
                                    )                                       
                                    props.onRecordUpdated({
                                        type: 'select',
                                        recordId: recordInput.record.id.toString(),
                                        selected: isMatch,
                                    })
                                })
                            }}
                        >
                            Apply
                        </button>
                        <button
                            className="btn btn-outline-secondary w-100 ms-2"
                            onClick={() => {
                                setCheckInFrom('')
                                setCheckInTo('')
                                Object.values(props.records).forEach((recordInput) => {
                                    props.onRecordUpdated({
                                        type: 'select',
                                        recordId: recordInput.record.id.toString(),
                                        selected: false,
                                    })
                                })
                            }}
                        >
                            Clear
                        </button>
                    </div>
                    <div className="col-auto">
                        <select
                            className="form-select"
                            value={bulkSelectValue}
                            disabled={!props.extractionAvailable}
                            onChange={(e) => {
                                const value = e.target.value
                                setBulkSelectValue(value)
                                if (!value) return
                                if (value === 'all') {
                                    props.onRecordUpdated({ type: 'select', selected: true })
                                } else if (value === 'clear') {
                                    props.onRecordUpdated({ type: 'select', selected: false })
                                } else {
                                    const matchHigh = value === 'high'
                                    Object.values(props.records).forEach((recordInput) => {
                                        const confidence = recordInput.confidenceScore
                                        const match =
                                            typeof confidence === 'number'
                                                ? matchHigh
                                                    ? confidence >= 80
                                                    : confidence < 80
                                                : false
                                        props.onRecordUpdated({
                                            type: 'select',
                                            recordId: recordInput.record.id.toString(),
                                            selected: match,
                                        })
                                    })
                                }
                            }}
                        >
                            <option value="">Bulk Select</option>
                            <option value="all">All</option>
                            <option value="high">High Confidence (&gt;=80)</option>
                            <option value="low">Low Confidence (&lt;80)</option>
                            <option value="clear">Clear</option>
                        </select>
                    </div>
                </div>
                {yearError && <div className="text-danger mt-2">{yearError}</div>}
            </div>

            <table className="invoice-reconciliation-table table table-sm table-responsive-md" style={{ minWidth: '100%' }}>
                <thead>
                    <tr>
                        <th>
                            <input
                                className="form-check-input"
                                onChange={(e) => props.onRecordUpdated({ type: 'select', selected: e.target.checked })}
                                type="checkbox"
                                checked={selectedRecords.length > 0}
                            />
                        </th>
                        <th>Record</th>
                        <th>Agency</th>
                        <th>Status</th>
                        <th>Confirmation Number</th>
                        <th>Name</th>
                        <th>Check In</th>
                        <th className="col-1">Nights</th>
                        <th className="col-2">Nightly Rate</th>
                        <th className="col-1">Commission Due</th>
                        <th className="col-2">Paid CT</th>
                        <th className="col-2">Payment Method</th>
                        <th className="col-2">Status</th>
                        <th className="col-2">Substatus</th>
                        <th>Reconcile</th>
                    </tr>
                </thead>
                <tbody>
                    {unreconciledRecords.concat(reconciledRecords).map((recordInput) => {
                        const values = {
                            days: recordInput.edited.days ?? recordInput.record.days.toString(),
                            rate: recordInput.edited.rate ?? recordInput.record.rate.toFixed(2),
                            commission: recordInput.edited.commission != undefined ? parseFloat(recordInput.edited.commission) : recordInput.record.commission,

                            paidCt: recordInput.edited.paidCt ?? recordInput.record.paidCt.toFixed(2),
                            paymentMethodId: recordInput.edited.paymentMethodId ?? (recordInput.record.paymentMethodId != undefined ? recordInput.record.paymentMethodId.toString() : ''), // default value to PaymentMethods.Check?
                            statusCode: recordInput.edited.statusCode ?? recordInput.record.statusCode.toString(),
                            substatusCode: recordInput.edited.substatusCode ?? (recordInput.record.substatusCode != undefined ? recordInput.record.substatusCode.toString() : ''),
                        }
                        const substatuses = props.recordStatuses.find((status) => status.code === parseInt(values.statusCode))?.substatuses

                        // used to render strikethrough on reconciled records
                        const commissionable = !RecordStatus.PROCESSED_STATUSES.includes(parseInt(values.statusCode))
                            || (RecordStatus.PROCESSED_STATUSES.includes(parseInt(values.statusCode)) &&
                                RecordStatus.PAID_STATUSES.includes(parseInt(values.statusCode)))
                        const inputDisabled = recordInput.reconciled

                        const confidence = recordInput.confidenceScore
                        const confidenceClass = getConfidenceClass(confidence)

                        const firstUnreconciledClass =
                        (reconciledRecords.length > 0 &&
                            unreconciledRecords.length > 0 &&
                            recordInput.record.id === reconciledRecords[0].record.id)
                            ? 'first-unreconciled'
                            : ''

                        const showOriginalDays = wasExtracted(recordInput, 'days')
                        const showOriginalRate = wasExtracted(recordInput, 'rate')
                        const showOriginalPaidCt = wasExtracted(recordInput, 'paidCt')
                        const showOriginalPaymentMethod = wasExtracted(recordInput, 'paymentMethodId')
                        const showOriginalStatus = wasExtracted(recordInput, 'statusCode')

                        return <tr key={recordInput.record.id} className={`${firstUnreconciledClass} ${confidenceClass}`.trim()}>
                            <td>
                                {!recordInput.reconciled &&
                                    <input
                                        checked={recordInput.selected}
                                        className='form-check-input'
                                        onChange={(e) => props.onRecordUpdated({ type: 'select', recordId: recordInput.record.id.toString(), selected: e.target.checked })}
                                        type='checkbox'
                                    />}
                            </td>
                            <td>
                                <Link to={`/records/${recordInput.record.id}`} target='_blank'>
                                    {recordInput.record.id}
                                </Link>
                            </td>
                            <td>{recordInput.record.agencyName}</td>
                            <td>{recordInput.record.statusName}</td>
                            <td>{recordInput.record.confirmationNumber}</td>
                            <td>{recordInput.record.guestLastName}, {recordInput.record.guestFirstName}</td>
                            <td>{recordInput.record.startDate.format('M/D/YYYY')}</td>
                            <td className={recordInput.edited.days != undefined ? 'edited' : ''}>
                                <input
                                    className="form-control"
                                    style={{ minWidth: '60px' }}
                                    disabled={inputDisabled}
                                    type="number"
                                    min={0}
                                    value={values.days}
                                    onChange={(e) => props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'days', value: e.target.value })}
                                    onBlur={(e) => {
                                        if (e.target.value === '') {
                                            props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'days', value: (0).toString() })
                                        }
                                    }}
                                />
                                {showOriginalDays &&
                                    renderOriginalText(recordInput.record.days)}
                            </td>
                            <td className={recordInput.edited.rate != undefined ? 'edited' : ''}>
                                <input
                                    className="form-control"
                                    style={{ minWidth: '100px' }}
                                    disabled={inputDisabled}
                                    type="number"
                                    step={0.01}
                                    min={0}
                                    value={values.rate}
                                    onChange={(e) => props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'rate', value: e.target.value })}
                                    onBlur={(e) => {
                                        if (e.target.value === '') {
                                            props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'rate', value: (0).toFixed(2) })
                                        }
                                    }}
                                />
                                {showOriginalRate &&
                                    renderOriginalText(currencyFormat(recordInput.record.rate))}
                            </td>
                            <td className={recordInput.edited.commission != undefined ? 'edited' : ''}>{((commissionable) && currencyFormat(values.commission)
                            ) || <s>{currencyFormat(values.commission)}</s>}</td>
                            <td className={recordInput.edited.paidCt != undefined ? 'edited' : ''}>
                                <input
                                    className="form-control"
                                    style={{ minWidth: '100px' }}
                                    disabled={inputDisabled}
                                    type='number'
                                    step={0.01}
                                    min={0}
                                    value={values.paidCt}
                                    onChange={(e) => props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'paidCt', value: e.target.value })}
                                    onBlur={(e) => {
                                        if (e.target.value === '') {
                                            props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'paidCt', value: (0).toFixed(2) })
                                        }
                                    }}
                                />
                                {showOriginalPaidCt &&
                                    renderOriginalText(currencyFormat(recordInput.record.paidCt))}
                            </td>
                            <td className={recordInput.edited.paymentMethodId != undefined ? 'edited' : ''}>
                                <select
                                    className='form-select'
                                    style={{ minWidth: '100px' }}
                                    disabled={inputDisabled}
                                    value={values.paymentMethodId}
                                    onChange={(e) => props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'paymentMethodId', value: e.target.value })}>
                                    <option value='' disabled={parseFloat(values.paidCt) !== 0}>N/A</option>
                                    {props.paymentMethods.map((method: PaymentMethod) => (
                                        <option key={method.id} value={method.id} disabled={parseFloat(values.paidCt) === 0}>
                                            {method.name}
                                        </option>
                                    ))}
                                </select>
                                {showOriginalPaymentMethod &&
                                    renderOriginalText(
                                        getPaymentMethodName(recordInput.record.paymentMethodId ?? 0),
                                    )}
                            </td>
                            <td className={recordInput.edited.statusCode != undefined ? 'edited' : ''}>
                                <select
                                    className='form-select'
                                    style={{ minWidth: '100px' }}
                                    disabled={inputDisabled}
                                    value={values.statusCode}
                                    onChange={(e) => props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'statusCode', value: e.target.value })}>
                                    {props.recordStatuses.map((status: RecordStatus) => (
                                        <option key={status.code} value={status.code}>
                                            {status.name}
                                        </option>
                                    ))}
                                </select>
                                {showOriginalStatus &&
                                    renderOriginalText(getStatusName(recordInput.record.statusCode))}
                            </td>
                            <td className={recordInput.edited.substatusCode != undefined ? 'edited' : ''}>
                                <select
                                    className='form-select'
                                    style={{ minWidth: '100px' }}
                                    disabled={inputDisabled}
                                    value={values.substatusCode}
                                    onChange={(e) => props.onRecordUpdated({ type: 'edit', recordId: recordInput.record.id.toString(), property: 'substatusCode', value: e.target.value })}>
                                    <option value=''>None</option>
                                    {substatuses && substatuses.map((substatus: RecordSubstatus) => (
                                        <option key={substatus.code} value={substatus.code}>
                                            {substatus.name}
                                        </option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                {!recordInput.reconciled && (
                                    <button
                                        className="btn btn-outline-success"
                                        onClick={() => {
                                            props.onRecordUpdated({ type: 'reconcile', recordId: recordInput.record.id.toString(), reconciled: true })
                                            props.onRecordUpdated({ type: 'select', recordId: recordInput.record.id.toString(), selected: false })
                                        }}>
                                        <CheckmarkIcon />
                                    </button>
                                )}

                                {recordInput.reconciled &&(
                                    // && (recordInput.editable || userHasRole(user, Roles.Executive)) &&
                                    <button
                                        className='btn btn-outline-danger'
                                        onClick={() => props.onRecordUpdated({ type: 'reconcile', recordId: recordInput.record.id.toString(), reconciled: false })}
                                    >
                                        <XIcon />
                                    </button>
                                )}
                            </td>
                        </tr>
                    })}
                </tbody>
            </table>
            <h4 className="mb-5 d-flex justify-content-end align-items-end">
                Total records: {Object.keys(props.records).length}
            </h4>
        </div>
    )
}
